import React from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material';

import type { ChannelMarkupTesterPanelProps } from './ChannelMarkupTesterPanel.types';

type Props = Pick<
  ChannelMarkupTesterPanelProps,
  'activeChannelMarkups' | 'isActiveChannelMarkupsLoading' | 'contentId' | 'summaryId'
>;

export default function ChannelMarkupActiveMarkupsAccordion({
  contentId,
  summaryId,
  activeChannelMarkups,
  isActiveChannelMarkupsLoading,
}: Props) {
  return (
    <Accordion disableGutters>
      <AccordionSummary aria-controls={`${contentId}-active`} id={`${summaryId}-active`}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Active channel markups
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isActiveChannelMarkupsLoading
              ? 'Loading active markups...'
              : `${activeChannelMarkups.length} active markup${activeChannelMarkups.length === 1 ? '' : 's'}`}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {activeChannelMarkups.length > 0 ? (
            activeChannelMarkups.map((markup) => (
              <Box
                key={markup.channelMarkupId}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, bgcolor: 'background.default' }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontWeight: 700 }}>{markup.channelMarkupId ?? 'Untitled markup'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {markup.version ?? 'v2'}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Conditions: {markup.rules?.conditions?.length ?? 0} · Markups: {markup.rules?.markups?.length ?? 0}
                  </Typography>
                  {markup.rules?.markups?.length ? (
                    <Stack spacing={0.25}>
                      {markup.rules.markups.map((row) => (
                        <Typography key={row.markupId ?? `${markup.channelMarkupId}-${row.markupType}`} variant="body2">
                          {row.markupId ?? row.markupType} · {row.markupType} · {row.currency ?? 'AUD'} ·{' '}
                          {row.markupValue ?? '--'}
                          {row.luxPlusMarkupValue != null ? ` / Lux Plus ${row.luxPlusMarkupValue}` : ''}
                        </Typography>
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No active channel markups were found.
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
